import { addMinutes } from 'date-fns/addMinutes';
import { setHours } from 'date-fns/setHours';
import { setMinutes } from 'date-fns/setMinutes';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Command } from '@/components/ui/cmdk/cmdk';
import { Popover } from '@/components/ui/popover';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { formatTimeFromHHmm } from '@/utils/date';

type TimeOption = {
  /** 24-hour format: "HH:mm" (e.g., "09:30", "14:00") */
  value: string;
  /** Locale-aware display string (e.g., "9:30 am", "14:00") */
  label: string;
};

type TimePickerPopoverProps = {
  selectedTime: string;
  onTimeSelected: (time: string) => void;
  triggerClassName?: string;
  triggerIcon?: 'clock' | 'chevronDown';
  triggerAriaLabel?: string;
};

export const TimePickerPopover = observer(
  ({
    selectedTime,
    onTimeSelected,
    triggerClassName,
    triggerIcon = 'clock',
    triggerAriaLabel,
  }: TimePickerPopoverProps) => {
    const [timeQuery, setTimeQuery] = useState('');
    const [open, setOpen] = useState(false);
    const selectedItemRef = useRef<HTMLDivElement | null>(null);

    const timeFormat = settingsStore.timeFormat;

    // Generate {value: "HH:mm", label: "localized"} pairs
    // value is stored/compared; label is displayed and searched
    const times: TimeOption[] = useMemo(() => {
      const timeList: TimeOption[] = [];
      let time = setHours(setMinutes(new Date(), 0), 0);
      for (let i = 0; i < 96; i++) {
        const hours = time.getHours().toString().padStart(2, '0');
        const minutes = time.getMinutes().toString().padStart(2, '0');
        const value = `${hours}:${minutes}`;
        timeList.push({
          value,
          label: formatTimeFromHHmm(value).toLowerCase(),
        });
        time = addMinutes(time, 15);
      }
      return timeList;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeFormat]);

    // Search by label (localized display string), return matching TimeOptions
    const filteredTimes = useMemo(() => {
      if (!timeQuery) return times;

      const query = timeQuery.toLowerCase().trim();
      if (query.length === 0) return times;

      const scoredTimes = times.map((timeOpt) => {
        const label = timeOpt.label;
        let score = 0;

        if (label === query) score += 100;
        else if (label.startsWith(query)) score += 50;
        else if (label.includes(query)) score += 25;

        // Handle both "3:30 pm" (12hr) and "15:30" (24hr) label formats
        const parts = label.split(' ');
        const timePart = parts[0];
        const period = parts[1]; // undefined for 24hr format

        const [hour, minute] = timePart.split(':');

        const patterns = [
          `${hour}:${minute}`,
          `${hour}${minute}`,
          `${hour}.${minute}`,
          `${hour} ${minute}`,
          hour,
          `${hour}:`,
        ];

        // Add period-specific patterns only if 12hr format
        if (period) {
          patterns.push(
            `${hour}:${minute} ${period}`,
            `${hour}${minute} ${period}`,
            `${hour}.${minute} ${period}`,
            `${hour} ${minute} ${period}`,
            `${hour}:${minute}${period}`,
            `${hour}${minute}${period}`,
            `${hour}.${minute}${period}`,
            `${hour} ${minute}${period}`,
            `${hour}a`,
            `${hour}p`,
            period,
            period === 'am' ? 'a' : 'p'
          );
        }

        patterns.forEach((pattern) => {
          if (query.includes(pattern) || pattern.includes(query)) {
            score += 30;
          }
        });

        if (query === hour) score += 40;
        if (query === `${hour}:${minute}`) score += 45;
        if (query === `${hour}${minute}`) score += 45;
        if (period) {
          if (query === period) score += 35;
          if (query === `${hour}${period.charAt(0)}`) score += 40;
        }

        return { timeOpt, score };
      });

      return scoredTimes
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((item) => item.timeOpt);
    }, [times, timeQuery]);

    // onTimeSelected receives HH:mm value (not localized label)
    const handleSelectTime = (value: string) => {
      onTimeSelected(value);
      setOpen(false);
      setTimeQuery('');
    };

    const handleOpenChange = (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        setTimeQuery('');
      }
    };

    useEffect(() => {
      if (!open) return;
      const frame = requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({ block: 'center' });
      });
      return () => cancelAnimationFrame(frame);
    }, [open]);

    // Display localized label for selected HH:mm value
    const displayLabel = formatTimeFromHHmm(selectedTime).toLowerCase();

    return (
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Tooltip label="Time of day">
          <Popover.Trigger asChild>
            <Button
              aria-label={
                triggerAriaLabel
                  ? `${triggerAriaLabel}: ${displayLabel}`
                  : undefined
              }
              variant="outline"
              className={cn('w-32 gap-2', triggerClassName)}
            >
              {triggerIcon === 'clock' && <Icons.clock className="size-3.5" />}
              <span className="flex-1 truncate">{displayLabel}</span>
              {triggerIcon === 'chevronDown' && (
                <Icons.chevronDown className="size-3.5" />
              )}
            </Button>
          </Popover.Trigger>
        </Tooltip>
        <Popover.Content
          className="w-48 p-0 mt-1"
          align="start"
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Stopped here, not at the input, so the root still handles the arrows and Enter */}
          <Command.Root onKeyDown={(e) => e.stopPropagation()}>
            <Command.Input
              placeholder="Search..."
              value={timeQuery}
              onValueChange={setTimeQuery}
            />
            <Command.Empty className="px-4 text-xs flex flex-col gap-1">
              <span>No times found.</span>
              <span>Try a different search.</span>
            </Command.Empty>
            <Command.List
              className="max-h-72 overflow-y-auto"
              onWheel={(e) => e.stopPropagation()}
            >
              <Command.Group>
                {filteredTimes.map((timeOpt) => (
                  <Command.Item
                    key={timeOpt.value}
                    ref={
                      selectedTime === timeOpt.value ? selectedItemRef : null
                    }
                    value={timeOpt.label}
                    data-selected={selectedTime === timeOpt.value}
                    data-current={selectedTime === timeOpt.value}
                    onSelect={() => handleSelectTime(timeOpt.value)}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span className="flex-1 text-sm truncate">
                      {timeOpt.label}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </Command.Root>
        </Popover.Content>
      </Popover.Root>
    );
  }
);
